import { useState, useEffect } from 'react'
import { expeditedReviewConfig } from '@/config/expeditedReviewConfig'
import { getApplicationNumber } from '@/services/intercom/intercomService'
import { ExpeditedReviewForm } from './ExpeditedReviewForm'

interface ApplicationConfirmationProps {
  applicationId: string
  applicationNumber: number | null
  applicantEmail: string
  applicantFirstName?: string | null
  applicantPhone?: string | null
  onLogout: () => void
}

export function ApplicationConfirmation({
  applicationId,
  applicationNumber: initialNumber,
  applicantEmail,
  applicantFirstName,
  applicantPhone,
  onLogout
}: ApplicationConfirmationProps) {
  const [applicationNumber, setApplicationNumber] = useState<number | null>(initialNumber)
  const [showExpeditedForm, setShowExpeditedForm] = useState(false)
  const [expeditedStartTime, setExpeditedStartTime] = useState<number | null>(null)
  const [expeditedSubmitted, setExpeditedSubmitted] = useState(false)
  const [loading, setLoading] = useState(!initialNumber)

  const config = expeditedReviewConfig.confirmationScreen

  // Fetch application number if not provided (returning applicant)
  useEffect(() => {
    if (initialNumber) {
      setApplicationNumber(initialNumber)
      setLoading(false)
      return
    }

    async function fetchNumber() {
      const result = await getApplicationNumber(applicationId)
      if (result) {
        setApplicationNumber(result.applicationNumber)
        setExpeditedSubmitted(result.expeditedReviewSubmitted)
      }
      setLoading(false)
    }

    fetchNumber()
  }, [applicationId, initialNumber])

  // Show the expedited review form
  if (showExpeditedForm && !expeditedSubmitted) {
    return (
      <ExpeditedReviewForm
        applicationId={applicationId}
        applicantEmail={applicantEmail}
        applicantFirstName={applicantFirstName}
        applicantPhone={applicantPhone}
        startTime={expeditedStartTime}
        onBack={() => setShowExpeditedForm(false)}
        onSubmitted={() => {
          setExpeditedSubmitted(true)
          setShowExpeditedForm(false)
        }}
      />
    )
  }

  // Confirmation screen
  return (
    <div className="min-h-screen starry-background flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
        {/* Green checkmark in yellow circle */}
        <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-5">
          <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
          </svg>
        </div>

        {/* Application Number */}
        <h2 className="text-2xl font-bold text-gray-900 mb-3">
          {loading ? (
            <span className="inline-block w-48 h-7 bg-gray-200 rounded animate-pulse" />
          ) : (
            applicationNumber ? config.title(applicationNumber) : 'Application Submitted'
          )}
        </h2>

        {/* Copy */}
        <p className="text-gray-600 mb-6 leading-relaxed text-sm">
          {config.body}
        </p>

        {/* Expedited Review Button or Submitted Badge */}
        {expeditedSubmitted ? (
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-50 text-green-700 rounded-lg text-sm font-medium border border-green-200 mb-4">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            Expedited review submitted
          </div>
        ) : (
          <button
            type="button"
            onClick={() => { setExpeditedStartTime(Date.now()); setShowExpeditedForm(true) }}
            className="w-full max-w-xs mx-auto block px-6 py-3.5 bg-[#4AA3CC] hover:bg-[#3B8DB3] text-white font-bold uppercase tracking-wide text-base rounded-xl shadow-lg hover:shadow-xl active:scale-[0.98] transition-all mb-4"
          >
            {config.expediteButton}
          </button>
        )}

        {/* Logout link */}
        <div className="mt-2">
          <button
            onClick={onLogout}
            className="text-sm text-gray-400 hover:text-gray-600 transition-colors"
          >
            Use a different email
          </button>
        </div>
      </div>
    </div>
  )
}
